import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Box } from '../../../core/miscellaneous/Box';
import { CloseButton } from '../../../core/button/CloseButton';
import { Text } from '../../../core/typography/Text';
import classes from './ResizableSidePanel.module.css';

export interface ResizableSidePanelProps {
  /** Whether the panel is open */
  readonly open: boolean;
  /** Callback when panel is closed */
  readonly onClose: () => void;
  /** Panel title */
  readonly title?: ReactNode;
  /** Panel content */
  readonly children: ReactNode;
  /** Minimum width in pixels */
  readonly minWidth?: number;
  /** Maximum width in pixels */
  readonly maxWidth?: number;
  /** Default width in pixels */
  readonly defaultWidth?: number;
  /** Header actions (rendered between title and close button) */
  readonly headerActions?: ReactNode;
  /** Position of the panel */
  readonly position?: 'right' | 'left';
  /** No padding around the content, for lists whose rows run edge to edge */
  readonly flush?: boolean;
}

/** How long the panel takes to open or close; matches the CSS transition. */
export const SIDE_PANEL_TRANSITION_MS = 250;

export const ResizableSidePanel = ({
  open,
  onClose,
  title,
  children,
  minWidth = 300,
  maxWidth = 600,
  defaultWidth = 400,
  headerActions,
  position = 'right',
  flush = false,
}: ResizableSidePanelProps) => {
  // Stays mounted through its close so it can animate out: `rendered` keeps
  // it in the tree, `shown` drives the width transition.
  const [rendered, setRendered] = useState(open);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      // Paint at width 0 first, then open, so the width transitions. Both
      // frames are cancelled on cleanup: closing between them must not let
      // the inner one reopen the panel.
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setShown(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setShown(false);
    const timer = setTimeout(
      () => setRendered(false),
      SIDE_PANEL_TRANSITION_MS
    );
    return () => clearTimeout(timer);
  }, [open]);

  const [panelWidth, setPanelWidth] = useState(defaultWidth);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(defaultWidth);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(true);
      startXRef.current = event.clientX;
      startWidthRef.current = panelWidth;
    },
    [panelWidth]
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = event.clientX - startXRef.current;
      let newWidth: number;

      if (position === 'right') {
        newWidth = startWidthRef.current - deltaX;
      } else {
        newWidth = startWidthRef.current + deltaX;
      }

      const clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      setPanelWidth(clampedWidth);
    },
    [isDragging, minWidth, maxWidth, position]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!rendered) {
    return null;
  }

  return (
    <Box
      className={classes.container}
      data-position={position}
      data-shown={shown || undefined}
      data-dragging={isDragging || undefined}
      style={{ '--panel-width': `${panelWidth}px` } as React.CSSProperties}
    >
      <Box
        className={classes.separator}
        data-dragging={isDragging || undefined}
        data-position={position}
        onMouseDown={handleMouseDown}
      />
      <Box className={classes.panel} data-position={position}>
        <Box className={classes.header}>
          {title && (
            <Text fw={600} size="sm" className={classes.title}>
              {title}
            </Text>
          )}
          <Box className={classes.headerActions}>
            {headerActions}
            <CloseButton onClick={onClose} size="sm" />
          </Box>
        </Box>
        <Box className={classes.content} data-flush={flush || undefined}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

ResizableSidePanel.displayName = 'ResizableSidePanel';
